import { Link as LinkIcon, PrecisionManufacturing as DeviceIcon, Speed as SpeedIcon } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import { Button, Chip, CircularProgress, Stack, Tooltip, Typography } from '@mui/material';
import { deviceLabel, HismithDeviceItem, HismithDeviceStatus } from '../../../../hooks/useHismith';

export const statusChipProps = (
    status?: HismithDeviceStatus,
): { label: string; color: 'success' | 'info' | 'default' | 'error'; title?: string } => {
    switch (status?.state) {
        case 'connected':
            return { label: 'Connected', color: 'success', title: `Speed ${status.speed ?? 0}%` };
        case 'connecting':
            return { label: 'Connecting', color: 'info' };
        case 'error':
            return { label: 'Error', color: 'error', title: status.error || 'Connection failed' };
        case 'offline':
            return { label: 'App offline', color: 'default', title: 'The HISMITH app left Remote Play. Paste a fresh link.' };
        case 'stopped':
        default:
            return { label: 'Not connected', color: 'default', title: 'Connects automatically when you go live or press Test.' };
    }
};

interface DeviceDetailsProps {
    device: HismithDeviceItem;
    onTest: () => void;
    isTesting: boolean;
    onUpdateLink: () => void;
}

const DeviceDetails = ({ device, onTest, isTesting, onUpdateLink }: DeviceDetailsProps) => {
    const chip = statusChipProps(device.status);

    return (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between" mb={2}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                <DeviceIcon fontSize="small" color={device.status?.state === 'connected' ? 'success' : 'disabled'} />
                <Typography variant="h6" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {deviceLabel(device)}
                </Typography>
                <Tooltip title={chip.title || ''}>
                    <Chip
                        size="small"
                        label={chip.label}
                        color={chip.color}
                        icon={device.status?.state === 'connecting' ? <CircularProgress size={12} color="inherit" /> : undefined}
                    />
                </Tooltip>
                {device.hasVibration && <Chip size="small" variant="outlined" label="Vibration" />}
                {device.supportsClimax && <Chip size="small" variant="outlined" label="Climax" />}
                <Typography variant="caption" color="text.secondary">
                    link …{device.tokenSuffix}
                </Typography>
            </Stack>
            <Stack direction="row" spacing={1}>
                <Tooltip title="Paste a new Remote Play link for this device">
                    <Button variant="outlined" size="small" startIcon={<LinkIcon />} onClick={onUpdateLink}>
                        Update link
                    </Button>
                </Tooltip>
                <Tooltip title="Run the machine at 60% for 3 seconds">
                    <LoadingButton loading={isTesting} variant="outlined" color="secondary" onClick={onTest} sx={{ minWidth: '40px' }}>
                        <SpeedIcon />
                    </LoadingButton>
                </Tooltip>
            </Stack>
        </Stack>
    );
};

export default DeviceDetails;
